import React, { useCallback, useMemo } from 'react';
import { StepProps } from '../components/Step.component';
import { Size } from '../enums/size.enum';
import { ButtonOption } from '../components/ButtonOption';
import { Option } from '../models/option';
import { BandSize, BraSize, CupSize, CupSizes, supportedBandSizes, unsupportedBandSizes as unsupportedBandSizesFn } from '../utils/bra-helper';
import { LongSize } from '../../product_detail/components/size.enum';
import { useProductSelection } from '../../hooks/useProductSelection';
import classNames from 'classnames';
import styles from './BraSizePickerViewRender.module.scss';

interface BraSizePickerProps extends StepProps {
  onChange: (option: Option) => () => void;
  key: string;
  nextStepKey: string;
  unsupportedStepKey: string;
  braSizeLookupFn: (braSize: BraSize) => Size | undefined;
}

export const BraSizePickerViewRender: React.FC<BraSizePickerProps> = (props) => {
  const { setSize, handle } = useProductSelection(props.selectionType, props.selectionKey);
  const preSelectedValue = props.stepValues.get(props.data.key);
  const [selectedBandSize, setSelectedBandSize] = React.useState<BandSize | undefined>(
    preSelectedValue && preSelectedValue.text ? (+preSelectedValue.text.slice(0, 2) as BandSize) : undefined,
  );
  const [selectedCupSize, setSelectedCupSize] = React.useState<CupSize | undefined>(
    preSelectedValue && preSelectedValue.text ? (preSelectedValue.text.substring(2).toUpperCase() as CupSize) : undefined,
  );
  const [userInteracted, setUserInteracted] = React.useState<boolean>(false);
  const bandSizes: BandSize[] = useMemo(() => supportedBandSizes(handle), [handle]);
  const unsupportedBandSizes: BandSize[] = useMemo(() => unsupportedBandSizesFn(handle), [handle]);

  const getNextStepKey = useCallback(() => {
    const selectedBraSize: BraSize | undefined = selectedBandSize && selectedCupSize ? `${selectedBandSize}${selectedCupSize}` : undefined;
    const sizeBasedOnBra = selectedBraSize && props.braSizeLookupFn(selectedBraSize);

    return sizeBasedOnBra !== Size['?'] && sizeBasedOnBra !== undefined ? props.nextStepKey : props.unsupportedStepKey;
  }, [props, selectedBandSize, selectedCupSize]);

  React.useEffect(() => {
    if (selectedBandSize && selectedCupSize && userInteracted) {
      const value = `${selectedBandSize}${selectedCupSize}`;
      const nextStepKey = getNextStepKey();

      if (!props.onSizeSelect && nextStepKey === props.unsupportedStepKey) {
        setSize(LongSize['3X']);
      }
      props.onChange({
        text: value,
        nextStepKey,
      })();
    }
  }, [getNextStepKey, props, selectedBandSize, selectedCupSize, setSize, userInteracted]);

  return (
    <div className={classNames(styles.stepContainer, styles.braStep)}>
      <div className={classNames(styles.stepText, 'hide-desktop')}>
        <span className={styles.subText}>{props.data.text}</span>
      </div>
      <div className={classNames(styles.stepText, 'hide-mobile')} style={{ textAlign: 'center' }}>
        <span className={styles.subText}>{props.data.text}</span>
      </div>
      <div className={styles.braOptions}>
        <div className={classNames('hide-desktop', styles.braOptionsSubHeader)}>BAND SIZE</div>
        <div className={classNames('hide-mobile', styles.braOptionsSubHeader)} style={{ textAlign: 'center' }}>
          What&apos;s your closest band size?
        </div>
        <div className={styles.braSizeCircles}>
          {bandSizes.map((bandSize, i) => {
            const isLast = unsupportedBandSizes.length === 0 && i === bandSizes.length - 1;
            return (
              <ButtonOption
                key={bandSize}
                option={{ text: `${bandSize}${isLast ? '+' : ''}`, value: '' + bandSize, nextStepKey: props.nextStepKey }}
                onClick={() => {
                  setSelectedBandSize(bandSize);
                  setUserInteracted(true);
                }}
                isSelected={bandSize === selectedBandSize}
                productHandle={props.productHandle}
                dataKey={props.data.key}
                activeMeasurement={props.activeMeasurement}
                stepValues={props.stepValues}
              />
            );
          })}
          {unsupportedBandSizes.map((bandSize, i) => {
            const isLast = i === unsupportedBandSizes.length - 1;
            return (
              <ButtonOption
                key={bandSize}
                option={{ text: `${bandSize}${isLast ? '+' : ''}`, value: '' + bandSize, nextStepKey: props.unsupportedStepKey }}
                onClick={() => {
                  setSelectedBandSize(bandSize);
                  setUserInteracted(true);
                }}
                isSelected={bandSize === selectedBandSize}
                productHandle={props.productHandle}
                dataKey={props.data.key}
                activeMeasurement={props.activeMeasurement}
                stepValues={props.stepValues}
              />
            );
          })}
        </div>
        <div className={classNames('hide-desktop', styles.braOptionsSubHeader)}>CUP SIZE</div>
        <div className={classNames('hide-mobile', styles.braOptionsSubHeader)} style={{ textAlign: 'center' }}>
          What&apos;s your cup size?
        </div>
        <div className={styles.braSizeCircles}>
          {CupSizes.map((cupSize) => (
            <ButtonOption
              key={cupSize}
              option={{ text: cupSize, value: cupSize, nextStepKey: getNextStepKey() }}
              onClick={() => {
                setSelectedCupSize(cupSize);
                setUserInteracted(true);
              }}
              isSelected={cupSize === selectedCupSize}
              productHandle={props.productHandle}
              dataKey={props.data.key}
              activeMeasurement={props.activeMeasurement}
              stepValues={props.stepValues}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default BraSizePickerViewRender;
